import { ClientActionService } from "@cervval.js/client-action/src/services/client.action.service";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { FaClasses, toggleVisible } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { CustomElement } from "@cervval.js/client-html5-spa/navigation";
import { CjsDropdownButton } from "@cervval.js/client-html5-ui/src/components/dropdownButton/dropdownButton";
import { CjsIconFa } from "@cervval.js/client-html5-ui/src/components/icon/iconFa";
import { IconFaSize } from "@cervval.js/client-html5-ui/src/components/icon/iconFa.tools";
import { ACTION_SERVICE } from "@cervval.js/common-action/src/services/action.service";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { addressToPlainAddress } from "@ehealth_tools/client-map/src/components/tools/map.tools";
import { BookingCommonUser } from "@ehealth_tools/common-booking/src/data/booking.dto";
import { isProfessional } from "@ehealth_tools/common-booking/src/data/booking.tools";
import { BOOKING_SERVICE } from "@ehealth_tools/common-booking/src/services/abstractCommonBooking.service";
import { formatInitials } from "@ehealth_tools/common/src/data/user/user.tools";
import { AbstractClientBookingService } from "../../services/abstractClientBooking.service";
import { _I18nBookingUserCard } from "./bookingUserCard.i18n";
export interface BookingUserCardOptions {
    user: BookingCommonUser | undefined;
    infoCallback: () => void;
}

const defaultBookingUserCardOptions: BookingUserCardOptions = {
    user: undefined,
    infoCallback: function (): void {
        throw new Error("Function not implemented.");
    }
};

@CustomElement({
    name: "booking-user-card",
    style: require("./bookingUserCard.scss"),
    template: require("./bookingUserCard.html"),
    i18n: require("./bookingUserCard.i18n").I18nBookingUserCard()
})
export class BookingUserCard<ElementTypes = unknown> extends WebComponent<_I18nBookingUserCard> {

    @Injected(BOOKING_SERVICE)
    private _bookingService!: AbstractClientBookingService;

    @Injected(ACTION_SERVICE)
    private _actionService!: ClientActionService<ElementTypes, unknown>;

    @TypedWebComponentOptions(defaultBookingUserCardOptions)
    public readonly options!: WebComponentOptions<BookingUserCardOptions>;

    @TypedHtmlElements({ ensureLoadedClasses: [CjsIconFa, CjsDropdownButton] })
    public readonly components!: {
        badge: HTMLDivElement,
        userImage: HTMLImageElement,
        header: HTMLDivElement,
        message: CjsIconFa,
        infos: CjsIconFa,
        specialtyTitle: HTMLDivElement,
        specialty: HTMLDivElement,
        addressTitle: HTMLDivElement,
        address: HTMLDivElement,
        phoneNumberTitle: HTMLDivElement,
        phoneNumber: HTMLDivElement
    };

    constructor(options?: BookingUserCardOptions) {
        super();
        this.options.setWithoutRefresh(options);

        this.components.message.options.setAndRefreshIfChanged({
            name: "fa-message",
            size: IconFaSize.SM,
            prefix: FaClasses.SOLID
        });
        this.components.infos.options.setAndRefreshIfChanged({
            name: "fa-circle-info",
            size: IconFaSize.SM,
            prefix: FaClasses.REGULAR
        });
        this.components.infos.clickEvent.on(() => {
            this.options.infoCallback();
        });
    }

    public override init(): Promise<void> | void {

    }

    private _setBadge(): void {
        if (this.options.user != null) {
            // Init text
            this.components.badge.innerText = formatInitials(this.options.user);
        }
    }

    /** */
    public override refresh(): void {
        const user = this.options.user;
        if (user != null) {
            this.components.header.innerText = `${user.givenName} ${user.familyName}`;
            this._setBadge();
            if (isProfessional(user)) {
                // Handle professional display
                const company = this._bookingService.getCompany(user);
                const specialty = this._bookingService.getProfessionalSpecialty(user);
                const imageUrl = this._bookingService.getSnippetURL(user) ?? "";
                if (imageUrl.length === 0) {
                    this._displayCardVisual("badge");
                }
                else {
                    this.components.userImage.src = imageUrl;
                    this._displayCardVisual("image");
                }
                this.components.specialtyTitle.innerText = this.i18n.specialtyTitle;
                this.components.specialty.innerText = specialty?.specialty ?? "";
                this.components.addressTitle.innerText = this.i18n.address;
                if(company != null) {
                    this.components.address.innerText = addressToPlainAddress(company);
                }
                this.components.phoneNumberTitle.innerText = this.i18n.phoneNumber;
                this.components.phoneNumber.innerText = user.phoneNumber ?? "";
            } else {
                // Handle user display
                this.components.specialtyTitle.innerText = "";
                this.components.specialty.innerText = "";
                this.components.addressTitle.innerText = "";
                this.components.address.innerText = "";
                this.components.phoneNumberTitle.innerText = this.i18n.phoneNumber;
                this.components.phoneNumber.innerText = user.phoneNumber ?? "";
            }


        }
    }

    /**
     * Display badge or profile picture of the user
     * @param elm 
     */
    private _displayCardVisual(elm: "image" | "badge"): void {
        toggleVisible(this.components.userImage, elm === "image");
        toggleVisible(this.components.badge, elm === "badge");
    }
}